import { StoredPacket } from '../storage/db';
import { FusionEngine } from '../storage/fusionEngine';
import { getSnrColor } from '../map/mapManager';
import { getNodeColor } from '../map/nodeColor';

/**
 * Configuration options required to instantiate TrafficFeedController.
 *
 * Why: Provides decoupled access to FusionEngine and user interaction callbacks without hardcoupling to Dashboard.
 */
export interface TrafficFeedControllerOptions {
  fusionEngine: FusionEngine;
  onPacketClick?: (packet: StoredPacket) => void;
  onDrawerStateChange?: (expanded: boolean) => void;
}

/**
 * Controller managing the Live Packet Traffic Feed drawer, table DOM virtual buffering,
 * requestAnimationFrame batching, and high-performance row rendering.
 *
 * Why: Decouples packet feed UI manipulation and animation frame timing from the central
 * Dashboard orchestrator, keeping mobile layout calculations throttled and testable.
 */
export class TrafficFeedController {
  private recentPackets: StoredPacket[] = [];
  private pendingTrafficRows: StoredPacket[] = [];
  private trafficRafId: number | null = null;
  private fusionEngine: FusionEngine;
  private onPacketClick?: (packet: StoredPacket) => void;
  private onDrawerStateChange?: (expanded: boolean) => void;

  constructor(options: TrafficFeedControllerOptions) {
    this.fusionEngine = options.fusionEngine;
    this.onPacketClick = options.onPacketClick;
    this.onDrawerStateChange = options.onDrawerStateChange;
  }

  /**
   * Initializes click handlers for the bottom traffic drawer collapse/expand toggle.
   *
   * Why: Operator can toggle feed visibility from the vehicle HUD; when collapsed, DOM
   * updates are stopped to conserve battery and CPU.
   */
  setupDrawerToggles(): void {
    const trafficHeader = document.getElementById('traffic-header');
    const trafficDrawer = document.getElementById('traffic-drawer');
    const trafficToggleIcon = document.getElementById('traffic-toggle-icon');

    if (trafficHeader && trafficDrawer && trafficToggleIcon) {
      trafficHeader.addEventListener('click', () => {
        const isExpanded = trafficDrawer.classList.toggle('expanded');
        document.body.classList.toggle('traffic-expanded', isExpanded);
        trafficToggleIcon.textContent = isExpanded ? '▼ Collapse' : '▲ Expand';

        if (isExpanded) {
          this.rebuildTrafficTable();
        }

        if (this.onDrawerStateChange) {
          this.onDrawerStateChange(isExpanded);
        }
      });
    }
  }

  /**
   * Ingests a new packet into the buffer and batches DOM insertion if drawer is visible.
   *
   * @param packet - Newly received and fused LoRa packet
   */
  addPacket(packet: StoredPacket): void {
    // Maintain in-memory packet buffer (latest first, max 100)
    this.recentPackets.unshift(packet);
    if (this.recentPackets.length > 100) {
      this.recentPackets.pop();
    }

    // Viewport-aware throttling: Only touch the DOM if the traffic drawer is actively expanded
    const tbody = document.getElementById('traffic-tbody');
    const trafficDrawer = document.getElementById('traffic-drawer');
    const isExpanded = trafficDrawer?.classList.contains('expanded');

    if (isExpanded && tbody) {
      this.pendingTrafficRows.push(packet);
      if (this.trafficRafId === null) {
        const scheduleFrame =
          typeof requestAnimationFrame === 'function'
            ? requestAnimationFrame
            : (cb: FrameRequestCallback) => setTimeout(cb, 16) as unknown as number;
        this.trafficRafId = scheduleFrame(() => {
          this.flushTrafficRows(tbody);
          this.trafficRafId = null;
        });
      }
    }
  }

  /**
   * Flushes queued packets into the table tbody using a DocumentFragment within a requestAnimationFrame tick.
   *
   * Why: Batching DOM writes into a single animation frame avoids layout thrashing when packets arrive in rapid succession.
   *
   * @param tbody - HTML table body element
   */
  private flushTrafficRows(tbody: HTMLElement): void {
    if (this.pendingTrafficRows.length === 0) return;

    const fragment: Node =
      typeof document.createDocumentFragment === 'function'
        ? document.createDocumentFragment()
        : document.createElement('tbody');

    // pendingTrafficRows has [earliest, ..., latest] in this frame.
    // Insert in reverse so the latest packet is at the top
    for (let i = this.pendingTrafficRows.length - 1; i >= 0; i--) {
      fragment.appendChild(this.createTrafficRow(this.pendingTrafficRows[i]));
    }
    this.pendingTrafficRows = [];

    const isDocFragment =
      (typeof DocumentFragment !== 'undefined' && fragment instanceof DocumentFragment) ||
      ('isFragment' in fragment && Boolean((fragment as { isFragment?: boolean }).isFragment));

    if (isDocFragment) {
      tbody.insertBefore(fragment, tbody.firstChild);
    } else {
      while (fragment.firstChild) {
        tbody.insertBefore(fragment.firstChild, tbody.firstChild);
      }
    }

    while (tbody.children.length > 100) {
      tbody.removeChild(tbody.lastChild!);
    }
  }

  /**
   * Rebuilds the traffic table DOM from recent in-memory packet buffer when expanding the collapsed drawer.
   *
   * Why: Allows completely suppressing DOM updates while drawer is hidden, saving substantial CPU and GPU cycles on mobile.
   */
  rebuildTrafficTable(): void {
    const tbody = document.getElementById('traffic-tbody');
    if (!tbody) return;

    tbody.innerHTML = '';
    const fragment: Node =
      typeof document.createDocumentFragment === 'function'
        ? document.createDocumentFragment()
        : document.createElement('tbody');

    for (const pkt of this.recentPackets) {
      fragment.appendChild(this.createTrafficRow(pkt));
    }

    const isDocFragment =
      (typeof DocumentFragment !== 'undefined' && fragment instanceof DocumentFragment) ||
      ('isFragment' in fragment && Boolean((fragment as { isFragment?: boolean }).isFragment));

    if (isDocFragment) {
      tbody.appendChild(fragment);
    } else {
      while (fragment.firstChild) {
        tbody.appendChild(fragment.firstChild);
      }
    }
  }

  /**
   * Constructs an individual HTMLTableRowElement representing a received LoRa packet in the live traffic stream.
   *
   * Why: Separating row construction allows both real-time batched DOM insertions and instantaneous full
   * table re-rendering from buffer memory when expanding the collapsed drawer.
   *
   * @param packet - The stored LoRa packet to render
   */
  private createTrafficRow(packet: StoredPacket): HTMLTableRowElement {
    const { color } = getSnrColor(packet.snr);
    const senderColor = getNodeColor(packet.from, packet.fromName);

    let relayDisplay = `<span style="color: #94a3b8;">${packet.relayName}</span>`;
    if (packet.relayNode && packet.relayNode !== 0) {
      const matchedRelay = this.fusionEngine.findNodeBy8BitRelay(
        packet.relayNode,
        packet.lat,
        packet.lon
      );
      const relayId = matchedRelay ? matchedRelay.num : (packet.relayNode & 0xff);
      const relayName = matchedRelay ? matchedRelay.shortName : packet.relayName;
      const relayColor = getNodeColor(relayId, relayName);
      relayDisplay = `<span style="color: ${relayColor}; font-weight: 600;"><span style="margin-right: 4px;">●</span>${packet.relayName}</span>`;
    }

    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${new Date(packet.timestamp).toLocaleTimeString()}</td>
      <td style="color: ${senderColor}; font-weight: bold;"><span style="margin-right: 4px;">●</span>${packet.fromName}</td>
      <td>${relayDisplay}</td>
      <td><span class="badge-snr" style="background: ${color}22; color: ${color}; border: 1px solid ${color};">${packet.snr} dB</span></td>
      <td>${packet.rssi} dBm</td>
      <td>${packet.hopsTaken}</td>
      <td>${packet.portName}</td>
      <td>${packet.hasGeo && packet.lat != null && packet.lon != null ? `<span title="Präzision: ±${Math.round(packet.accuracy || 0)}m">${packet.lat.toFixed(5)}, ${packet.lon.toFixed(5)}</span>` : '<span style="color: #ff003c;">No GPS</span>'}</td>
      <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">${packet.payloadSummary}</td>
    `;

    // Click to center map and draw inspection vector to sending node
    row.addEventListener('click', () => {
      if (this.onPacketClick) {
        this.onPacketClick(packet);
      }
    });

    return row;
  }

  /**
   * Resets the in-memory packet buffer and clears traffic table rows.
   *
   * Why: Ensures clean state synchronization when active session is cleared or switched.
   */
  clear(): void {
    this.recentPackets = [];
    this.pendingTrafficRows = [];
    if (this.trafficRafId !== null) {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(this.trafficRafId);
      } else {
        clearTimeout(this.trafficRafId);
      }
      this.trafficRafId = null;
    }
    const tbody = document.getElementById('traffic-tbody');
    if (tbody) tbody.innerHTML = '';
  }

  /**
   * Retrieves the in-memory buffered recent packets.
   *
   * @returns Array of recent StoredPacket objects
   */
  getRecentPackets(): StoredPacket[] {
    return this.recentPackets;
  }
}
